import { useCallback, useMemo, useRef, useState } from 'react'
import type { Invite } from '../hooks/useInvites'
import { INVITE_CATEGORIES } from '../hooks/useInvites'
import { useToast } from '../hooks/useToast'

interface InviteListProps {
  invites: Invite[]
  loading: boolean
  selectedId: string | null
  onSelect: (invitationId: string) => void
  onRespond: (invite: Invite, action: 'accept' | 'ignore') => Promise<string>
  hasMore?: boolean
  paginating?: boolean
  onLoadMore?: () => void
  activeCategory: string
  onCategoryChange: (category: string) => void
  categoryCounts: Record<string, number>
  scanning?: boolean
  scannedCount?: number
  scanComplete?: boolean
}

export default function InviteList({
  invites,
  loading,
  selectedId,
  onSelect,
  onRespond,
  hasMore,
  paginating,
  onLoadMore,
  activeCategory,
  onCategoryChange,
  categoryCounts,
  scanning,
  scannedCount,
  scanComplete,
}: InviteListProps) {
  const scrollRef = useRef<HTMLDivElement>(null)

  const handleScroll = useCallback(() => {
    if (!hasMore || paginating || !onLoadMore || !scrollRef.current || scanning) return
    const el = scrollRef.current
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 100) {
      onLoadMore()
    }
  }, [hasMore, paginating, onLoadMore, scanning])

  // Group invites by normalized time buckets (matching inbox: Today, Yesterday, Last 7 days, etc.)
  const grouped = useMemo(() => {
    const map = new Map<string, Invite[]>()
    for (const inv of invites) {
      const key = normalizeGroupHeader(inv.sentTimeLabel)
      const list = map.get(key) || []
      list.push(inv)
      map.set(key, list)
    }
    return [...map.entries()]
  }, [invites])

  // Chips that have results (always show ALL)
  const visibleChips = useMemo(
    () => INVITE_CATEGORIES.filter((c) => c.key === 'ALL' || (categoryCounts[c.key] || 0) > 0),
    [categoryCounts]
  )

  if (loading && invites.length === 0) {
    return (
      <div className="flex flex-1 flex-col overflow-hidden">
        <FilterChips
          chips={visibleChips}
          active={activeCategory}
          counts={categoryCounts}
          onChange={onCategoryChange}
        />
        <div className="flex-1 px-4 pt-1">
          {[0.9, 0.7, 0.5, 0.35, 0.2].map((opacity, i) => (
            <div key={i} className="flex items-center gap-3 py-3" style={{ opacity }}>
              <div className="h-10 w-10 shrink-0 animate-pulse rounded-full bg-zinc-200" />
              <div className="flex flex-1 flex-col gap-1.5">
                <div className="h-2.5 animate-pulse rounded bg-zinc-200" style={{ width: `${45 + (i % 3) * 15}%` }} />
                <div className="h-2 animate-pulse rounded bg-zinc-100" style={{ width: `${60 + (i % 3) * 10}%` }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  if (invites.length === 0 && !scanning) {
    return (
      <div className="flex flex-1 flex-col overflow-hidden">
        <FilterChips
          chips={visibleChips}
          active={activeCategory}
          counts={categoryCounts}
          onChange={onCategoryChange}
        />
        <div className="flex flex-1 items-center justify-center p-6 text-center">
          <div>
            <p className="text-2xl text-zinc-200">🤝</p>
            <p className="mt-2 text-xs text-zinc-400">
              {activeCategory === 'ALL' ? 'No pending invitations' : `No ${activeCategory.toLowerCase()} invitations found`}
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <FilterChips
        chips={visibleChips}
        active={activeCategory}
        counts={categoryCounts}
        onChange={onCategoryChange}
      />
      <div ref={scrollRef} className="flex-1 overflow-y-auto" onScroll={handleScroll}>
        {grouped.map(([label, items]) => (
          <div key={label}>
            <div className="sticky top-0 z-10 bg-white/95 px-4 py-1.5 backdrop-blur-sm">
              <span className="text-[10px] font-medium uppercase tracking-wider text-zinc-400">{label}</span>
            </div>
            {items.map((inv) => (
              <InviteRow
                key={inv.invitationId || inv.entityUrn || inv.name}
                invite={inv}
                selected={selectedId === inv.invitationId}
                onSelect={() => inv.invitationId && onSelect(inv.invitationId)}
                onRespond={onRespond}
              />
            ))}
          </div>
        ))}

        {/* Pagination loading */}
        {paginating && !scanning && (
          <div className="py-3 text-center text-xs text-zinc-400">Loading more...</div>
        )}

        {/* Scan progress indicator */}
        {scanning && (
          <div className="px-4 py-3 text-center">
            <div className="flex items-center justify-center gap-2">
              <div className="flex items-center gap-[3px]">
                <div className="h-1 w-1 animate-pulse rounded-full bg-zinc-400" style={{ animationDelay: '0ms' }} />
                <div className="h-1 w-1 animate-pulse rounded-full bg-zinc-400" style={{ animationDelay: '200ms' }} />
                <div className="h-1 w-1 animate-pulse rounded-full bg-zinc-400" style={{ animationDelay: '400ms' }} />
              </div>
              <span className="text-[11px] text-zinc-400">Scanning invites... ({scannedCount || 0} scanned)</span>
            </div>
          </div>
        )}
        {!scanning && scanComplete && activeCategory !== 'ALL' && (
          <div className="px-4 py-3 text-center">
            <span className="text-[11px] text-zinc-400">
              All invites scanned · {categoryCounts[activeCategory] || 0} {activeCategory.toLowerCase()} found
            </span>
          </div>
        )}
      </div>
    </div>
  )
}

function FilterChips({
  chips,
  active,
  counts,
  onChange,
}: {
  chips: readonly { key: string; label: string }[]
  active: string
  counts: Record<string, number>
  onChange: (key: string) => void
}) {
  return (
    <div className="flex gap-1.5 overflow-x-auto border-b border-zinc-100 px-4 py-2.5">
      {chips.map((chip) => {
        const isActive = active === chip.key
        const count = counts[chip.key] || 0
        return (
          <button
            key={chip.key}
            onClick={() => onChange(chip.key)}
            className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors ${
              isActive
                ? 'bg-zinc-900 text-white'
                : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
            }`}
          >
            {chip.label}{count > 0 ? ` (${count})` : ''}
          </button>
        )
      })}
    </div>
  )
}

function InviteRow({
  invite,
  selected,
  onSelect,
  onRespond,
}: {
  invite: Invite
  selected: boolean
  onSelect: () => void
  onRespond: (invite: Invite, action: 'accept' | 'ignore') => Promise<string>
}) {
  const [hovered, setHovered] = useState(false)
  const { toast, show: showToast } = useToast({ durationMs: 2000 })

  const handleRespond = (action: 'accept' | 'ignore') => {
    onRespond(invite, action)
      .then((name) => {
        if (action === 'accept') showToast(`Connected with ${name}`)
      })
      .catch(() => showToast('Failed — try again'))
  }

  return (
    <div
      onClick={onSelect}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      role="button"
      tabIndex={0}
      className={`relative flex w-full cursor-pointer items-center gap-3 px-4 py-2.5 text-left transition-colors ${
        selected ? 'bg-zinc-100' : hovered ? 'bg-zinc-50' : ''
      }`}
    >
      {invite.profileImage ? (
        <img src={invite.profileImage} alt="" className="h-10 w-10 shrink-0 rounded-full" />
      ) : (
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-zinc-200 text-xs font-semibold text-zinc-500">
          {getInitials(invite.name)}
        </div>
      )}

      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-1.5">
            <span className="truncate text-[13px] font-semibold text-zinc-900">{invite.name}</span>
            {invite.message && (
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
            )}
          </div>
          {!hovered && invite.mutualConnections && (
            <span className="shrink-0 rounded-full bg-blue-50 px-1.5 py-0.5 text-[9px] font-medium text-blue-600">
              {extractMutualCount(invite.mutualConnections)}
            </span>
          )}
        </div>
        <p className="truncate text-[11px] text-zinc-500">{invite.headline}</p>
      </div>

      {hovered && (
        <div className="flex shrink-0 items-center gap-1.5">
          <button
            onClick={(e) => { e.stopPropagation(); handleRespond('accept') }}
            className="rounded-md bg-zinc-900 px-2.5 py-1 text-[11px] font-medium text-white hover:bg-zinc-800"
          >
            Accept
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); handleRespond('ignore') }}
            className="rounded-md bg-zinc-100 px-2.5 py-1 text-[11px] font-medium text-zinc-600 hover:bg-zinc-200"
          >
            Ignore
          </button>
        </div>
      )}

      {toast && (
        <span className="absolute right-4 top-1/2 -translate-y-1/2 rounded bg-zinc-900 px-2 py-1 text-[10px] text-white">
          {toast}
        </span>
      )}
    </div>
  )
}

function getInitials(name: string): string {
  return name.split(' ').map((w) => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase()
}

function extractMutualCount(text: string): string {
  const match = text.match(/(\d+)/)
  if (!match) return text
  return `${parseInt(match[1], 10)} mutual`
}

// Normalize LinkedIn's "X days/weeks/months ago" into inbox-style group headers:
// Today, Yesterday, Last 7 days, This month, month names, year
function normalizeGroupHeader(label?: string | null): string {
  if (!label) return 'Other'
  const lower = label.toLowerCase()
  if (lower.includes('today') || lower === '1 day ago') return 'Today'
  if (lower === '2 days ago' || lower.includes('yesterday')) return 'Yesterday'

  const daysMatch = lower.match(/^(\d+)\s*days?\s*ago$/)
  if (daysMatch) {
    const days = parseInt(daysMatch[1], 10)
    if (days <= 7) return 'Last 7 days'
    if (days <= 30) return 'This month'
    const d = new Date(); d.setDate(d.getDate() - days)
    return d.getFullYear() === new Date().getFullYear()
      ? d.toLocaleString('default', { month: 'long' })
      : `${d.toLocaleString('default', { month: 'long' })} ${d.getFullYear()}`
  }

  const weeksMatch = lower.match(/^(\d+)\s*weeks?\s*ago$/)
  if (weeksMatch) {
    const weeks = parseInt(weeksMatch[1], 10)
    if (weeks === 1) return 'Last 7 days'
    if (weeks <= 4) return 'This month'
    const d = new Date(); d.setDate(d.getDate() - weeks * 7)
    return d.getFullYear() === new Date().getFullYear()
      ? d.toLocaleString('default', { month: 'long' })
      : `${d.toLocaleString('default', { month: 'long' })} ${d.getFullYear()}`
  }

  const monthsMatch = lower.match(/^(\d+)\s*months?\s*ago$/)
  if (monthsMatch) {
    const months = parseInt(monthsMatch[1], 10)
    const d = new Date(); d.setMonth(d.getMonth() - months)
    return d.getFullYear() === new Date().getFullYear()
      ? d.toLocaleString('default', { month: 'long' })
      : `${d.toLocaleString('default', { month: 'long' })} ${d.getFullYear()}`
  }

  return 'Other'
}
